import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Image,
  Text,
  VStack,
  useBreakpoint,
} from "@chakra-ui/react";
import { useRouter } from "next/router";
import type { ComponentProps } from "react";
import { LargeBreak, MediumBreak, SmallBreak } from "../helpers";
import type { IServices } from "./data";
import { services } from "./data";

const Services = () => {
  const breakpoint = useBreakpoint();

  return (
    <Box bgColor="rgba(240, 237, 236, 0.2)" width="full">
      <Flex
        py={"100px"}
        mx="auto"
        width={{ lg: "full", base: "unset" }}
        gap={{ lg: "100px", base: "80px" }}
        maxWidth={"1440px"}
        px={{ lg: "unset", base: "16px" }}
        direction={"column"}
      >
        <Box mx="auto" width={"full"}>
          <Header />
        </Box>
        <Box>
          {services.map((data, idx) => {
            const reverse = (idx + 1) % 2 === 0;
            return (
              <Grid
                gridTemplateColumns={{ lg: "1fr 1fr" }}
                gridTemplateRows={{ base: "0.5fr" }}
                gridRowGap={{ base: "40px" }}
                pt={{ lg: "unset", base: idx === 0 ? "24px" : "104px" }}
                key={data.id}
              >
                <Center
                  order={
                    (breakpoint === "lg" || breakpoint === "2xl" || breakpoint === "xl") && reverse
                      ? 2
                      : 1
                  }
                  {...(reverse && {
                    pr:
                      breakpoint === "lg"
                        ? "24px"
                        : breakpoint === "2xl" || breakpoint === "xl"
                        ? "160px"
                        : "0px",
                    pl:
                      breakpoint === "lg"
                        ? "64px"
                        : breakpoint === "2xl" || breakpoint === "xl"
                        ? "64px"
                        : "0px",
                  })}
                  {...(!reverse && {
                    pl:
                      breakpoint === "lg"
                        ? "24px"
                        : breakpoint === "2xl" || breakpoint === "xl"
                        ? "160px"
                        : "0px",
                    pr:
                      breakpoint === "lg"
                        ? "64px"
                        : breakpoint === "2xl" || breakpoint === "xl"
                        ? "64px"
                        : "0px",
                  })}
                >
                  <Card {...data} />
                </Center>
                <Flex
                  order={
                    (breakpoint === "lg" || breakpoint === "2xl" || breakpoint === "xl") && reverse
                      ? 1
                      : 2
                  }
                  minHeight={{ xl: "600px", lg: "500px", base: "240px" }}
                  w={"full"}
                  h="full"
                >
                  <Image src={data.image} alt={`image-${data.name}`} h="full" objectFit={"cover"} />
                </Flex>
              </Grid>
            );
          })}
        </Box>
      </Flex>
    </Box>
  );
};

export default Services;

const Card = (props: IServices) => {
  const { description, goals, name, type } = props;
  const route = useRouter();
  return (
    <VStack align={"start"} fontFamily={"'Inter', sans-serif"}>
      <Text
        textTransform={"uppercase"}
        bgColor={"rgba(249, 210, 9, 0.8)"}
        borderRadius={"16px"}
        padding={"4px 12px"}
        fontSize={"14px"}
        lineHeight={"14px"}
        color="#0D3050"
        mb="12px"
      >
        {type}
      </Text>
      <Text
        fontSize={{ xl: "40px", lg: "32px", base: "32px" }}
        lineHeight={{ xl: "44px", lg: "36px", base: "36px" }}
        letterSpacing={"-0.02em"}
        color="#0D3050"
        fontFamily={"Georgia"}
        pb="12px"
      >
        {name}
      </Text>
      <Text
        fontSize={{ xl: "18px", lg: "16px", base: "16px" }}
        lineHeight={{ xl: "28px", lg: "24px", base: "24px" }}
        color="rgba(13, 48, 80, 0.8)"
        pb="20px"
        w="full"
        letterSpacing={{ xl: "unset", lg: "0.02em", base: "0.02em" }}
      >
        {description}
      </Text>
      <Flex direction={"column"} gap="16px">
        {goals.map((goal, idx) => {
          return (
            <Flex key={idx} gap="14px" align={"center"}>
              <Box>
                <CheckIcon width="20px" height="20px" />
              </Box>
              <Text
                fontSize={{ xl: "18px", lg: "16px" }}
                lineHeight={{ xl: "28px", lg: "24px" }}
                color="rgba(13, 48, 80, 0.8)"
              >
                {goal}
              </Text>
            </Flex>
          );
        })}
      </Flex>
      <Flex justify={"start"} pt="32px" w={{ lg: "unset", base: "full" }}>
        <Button
          bgColor="#F9D209"
          onClick={() => route.push("/programs")}
          w={{ lg: "unset", base: "full" }}
        >
          Learn more
        </Button>
      </Flex>
    </VStack>
  );
};
const Header = () => {
  return (
    <Flex direction={"column"} fontFamily={"'Inter', sans-serif"} w="full">
      <Text
        mb={{ lg: "16px", base: "5px" }}
        color="rgba(13, 48, 80, 0.8)"
        textTransform={"uppercase"}
        letterSpacing={"0.05em"}
        w="full"
        textAlign={"center"}
        fontSize={{ lg: "18px", base: "14px" }}
        lineHeight={{ lg: "18px", base: "14px" }}
      >
        our services
      </Text>
      <Text
        mb={{ lg: "24px", base: "16px" }}
        color="#0D3050"
        letterSpacing={"-0.02em"}
        w="full"
        textAlign={"center"}
        fontSize={{ lg: "40px", base: "32px" }}
        lineHeight={{ lg: "44px", base: "36px" }}
        fontFamily={"Georgia"}
      >
        We put people <SmallBreak /> at the center of <SmallBreak />
        everything we do
      </Text>
      <Text
        color="rgba(13, 48, 80, 0.8)"
        letterSpacing={"0.02em"}
        textAlign={"center"}
        fontSize={{ lg: "24px", base: "16px" }}
        lineHeight={{ lg: "32px", base: "24px" }}
      >
        Harness the power of coaching to nurture employee growth <MediumBreak /> <LargeBreak /> and
        create a more collaborative, productive workplace.
      </Text>
    </Flex>
  );
};
export function CheckIcon(props: ComponentProps<"svg">) {
  return (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M7.5 12.46L10.5 15.46L16.5 9.45996M22 12.46C22 17.9828 17.5228 22.46 12 22.46C6.47715 22.46 2 17.9828 2 12.46C2 6.93711 6.47715 2.45996 12 2.45996C17.5228 2.45996 22 6.93711 22 12.46Z"
        stroke="#0D3050"
        stroke-opacity="0.6"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}
